import { $amplitudeExperimentsAtom } from '@client/core/atoms/amplitude.js';
import {
  AfsPlacementId,
  Brand,
  ConsentStatus,
  ExperimentVariants,
  FinnLegendaryAfsPlacementId,
  FinnLegendaryVertical,
  Vertical
} from '@schibsted-nmp/advertising-shared';

import { $config } from '../../core/atoms/config.js';
import { debug } from '../.debug.js';

/** Custom channels can be edited at {@link https://adsense.google.com}. */
const channelsConfigMap: {
  readonly [K in Brand]?: {
    readonly vertical: {
      readonly [K in Vertical | FinnLegendaryVertical]?: string;
    };

    readonly consent: {
      readonly [K in ConsentStatus]: string;
    };

    readonly placement: {
      readonly [K in AfsPlacementId | FinnLegendaryAfsPlacementId]?: string;
    };

    readonly thirdPlacementExperiment: {
      readonly [K in ExperimentVariants]?: {
        readonly [K in AfsPlacementId | FinnLegendaryAfsPlacementId]?: string;
      };
    };
  };
} = {
  [Brand.Finn]: {
    vertical: {
      [Vertical.Recommerce]: '1541148774'
    },
    consent: {
      [ConsentStatus.Accepted]: '4684201339',
      [ConsentStatus.Rejected]: '9888426555'
    },
    placement: {
      [AfsPlacementId.AfsContainer1]: '3018295631',
      [AfsPlacementId.AfsContainerBottom]: '2926989810'
    },
    thirdPlacementExperiment: {}
  },

  [Brand.Dba]: {
    vertical: {
      [Vertical.Recommerce]: '7595169273'
    },
    consent: {
      [ConsentStatus.Accepted]: '5124122443',
      [ConsentStatus.Rejected]: '5399454618'
    },
    placement: {
      [AfsPlacementId.AfsContainer1]: '1460209601',
      [AfsPlacementId.AfsContainer2]: '9636191445',
      [AfsPlacementId.AfsContainerBottom]: '4386552005'
    },
    thirdPlacementExperiment: {
      [ExperimentVariants.Control]: {
        [AfsPlacementId.AfsContainer1]: '9858566617',
        [AfsPlacementId.AfsContainerBottom]: '7559706330'
      },
      [ExperimentVariants.Treatment]: {
        [AfsPlacementId.AfsContainer1]: '7643466817',
        [AfsPlacementId.AfsContainerBottom]: '1788504907'
      }
    }
  },

  [Brand.Tori]: {
    vertical: {
      [Vertical.Recommerce]: '6734179364'
    },
    consent: {
      [ConsentStatus.Accepted]: '9803002315',
      [ConsentStatus.Rejected]: '1972613759'
    },
    placement: {
      [AfsPlacementId.AfsContainer1]: '4031994407',
      [AfsPlacementId.AfsContainerBottom]: '5839706570'
    },
    thirdPlacementExperiment: {}
  },

  [Brand.Blocket]: {
    vertical: {
      [Vertical.Recommerce]: '1809641818'
    },
    consent: {
      [ConsentStatus.Accepted]: '9864936243',
      [ConsentStatus.Rejected]: '3606649654'
    },
    placement: {
      [AfsPlacementId.AfsContainer1]: '9230511427',
      [AfsPlacementId.AfsContainerBottom]: '7852305986'
    },
    thirdPlacementExperiment: {}
  }
};

function* getAfsChannels(
  placementId: AfsPlacementId | FinnLegendaryAfsPlacementId
) {
  const { brand, consent, vertical } = $config.get() ?? {};

  if (!brand) throw new Error('Brand missing in config');
  if (!vertical) throw new Error('Vertical missing in config');

  const brandConfig = channelsConfigMap[brand];

  if (!brandConfig) {
    throw new Error(`No channels config found for brand ${brand}`);
  }

  if (consent) yield brandConfig.consent[consent];

  const verticalChannel = brandConfig.vertical[vertical];
  if (verticalChannel) yield verticalChannel;

  const placementChannel = brandConfig.placement[placementId];
  if (placementChannel) yield placementChannel;

  const {
    experiments: { 'advt-adsense-3rd-placement': thirdPlacementVariant }
  } = $amplitudeExperimentsAtom.get();

  if (thirdPlacementVariant) {
    const { [thirdPlacementVariant]: thirdPlacementVariantMap } =
      brandConfig.thirdPlacementExperiment;

    if (thirdPlacementVariantMap) {
      const thirdPlacementChannel = thirdPlacementVariantMap[placementId];
      if (thirdPlacementChannel) yield thirdPlacementChannel;
    }
  }
}

export default function getAfsChannel(
  placementId: AfsPlacementId | FinnLegendaryAfsPlacementId
): string {
  try {
    const channelsString = Array.from(getAfsChannels(placementId))
      .filter(Boolean)
      .join('+');

    return channelsString;
  } catch (error) {
    debug.message`Failed to get AFS channels for ${placementId} placement!`(
      error
    ).error();

    return '';
  }
}
